import moment from 'moment';

// Modelo da Pulse Rail (fase 3.1 — docs/plans/overhaul-ux-atlas-vivo.md).
// Normaliza eventos ao longo de um período: posição temporal (0..1), grupo
// visual e densidade por bucket. Função pura e testável; a renderização
// (SVG/DOM) e o sync com o mapa (3.2) consomem este modelo.

// Grupos visuais conforme o sistema de tokens do Atlas Vivo:
//   movement (jade) · place (coral) · moment (sun) · data (sky)
export const PULSE_GROUPS = ['movement', 'place', 'moment', 'data'];

const TYPE_TO_GROUP = {
  checkin: 'place',
  photos: 'place',
  walking: 'movement',
  running: 'movement',
  cycling: 'movement',
  transport: 'movement',
  car: 'movement',
  health: 'movement',
  google_maps: 'movement',
  concerts: 'moment',
  movie: 'moment',
  shows: 'moment',
  spotify: 'data',
  apple_music: 'data',
  diary: 'data',
};

export const groupForType = (type) => TYPE_TO_GROUP[type] || 'data';

// Largura do destaque ao hover/scrub — ~1 bucket da rail (48 bins padrão).
export const DEFAULT_RAIL_WINDOW_FRACTION = 1 / 48;
export const RAIL_DIMMED_OPACITY = 0.12;
export const RAIL_HIGHLIGHT_OPACITY = 1;

const toMillis = (value) => {
  if (value == null) return NaN;
  const m = moment(value);
  return m.isValid() ? m.valueOf() : NaN;
};

const toMillisFast = (value) => {
  if (value == null) return NaN;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : NaN;
};

// events: [{ date, type }]. Retorna { points, buckets, total, groupTotals,
// maxBucketCount }. Vazio/inválido devolve um modelo estável e vazio.
export function buildPulseRailModel(events, options = {}) {
  const { start, end, bucketCount = 48 } = options;
  const startMs = toMillis(start);
  const endMs = toMillis(end);
  const bins = Math.max(1, Math.floor(bucketCount));

  const emptyBuckets = Array.from({ length: bins }, (_, index) => ({
    index,
    count: 0,
    groups: { movement: 0, place: 0, moment: 0, data: 0 },
  }));
  const emptyModel = {
    points: [],
    buckets: emptyBuckets,
    total: 0,
    groupTotals: { movement: 0, place: 0, moment: 0, data: 0 },
    maxBucketCount: 0,
    valid: Number.isFinite(startMs) && Number.isFinite(endMs) && endMs > startMs,
  };

  if (!Array.isArray(events) || !emptyModel.valid) {
    return emptyModel;
  }

  const span = endMs - startMs;
  const points = [];
  const buckets = emptyBuckets.map((bucket) => ({
    ...bucket,
    groups: { ...bucket.groups },
  }));
  const groupTotals = { movement: 0, place: 0, moment: 0, data: 0 };

  for (const event of events) {
    const ms = toMillis(event && event.date);
    if (!Number.isFinite(ms) || ms < startMs || ms > endMs) continue;

    const position = span === 0 ? 0 : (ms - startMs) / span;
    const group = groupForType(event.type);
    const bucketIndex = Math.min(bins - 1, Math.floor(position * bins));

    points.push({ position, group, type: event.type, date: event.date });
    buckets[bucketIndex].count += 1;
    buckets[bucketIndex].groups[group] += 1;
    groupTotals[group] += 1;
  }

  const maxBucketCount = buckets.reduce((max, bucket) => Math.max(max, bucket.count), 0);

  return {
    points,
    buckets,
    total: points.length,
    groupTotals,
    maxBucketCount,
    valid: true,
  };
}

// Converte posição 0..1 + período em janela temporal (ms).
export function positionToMillisWindow(
  position,
  start,
  end,
  windowFraction = DEFAULT_RAIL_WINDOW_FRACTION,
) {
  const startMs = toMillis(start);
  const endMs = toMillis(end);
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) return null;
  if (!Number.isFinite(position)) return null;
  const span = endMs - startMs;
  const half = (windowFraction * span) / 2;
  const centerMs = startMs + position * span;
  return { centerMs, startMs: centerMs - half, endMs: centerMs + half };
}

export function isDateNearPosition(date, position, windowFraction, range) {
  const window = positionToMillisWindow(
    position,
    range?.start,
    range?.end,
    windowFraction,
  );
  if (!window) return false;
  const ms = toMillisFast(date);
  return Number.isFinite(ms) && ms >= window.startMs && ms <= window.endMs;
}

// Filtra pontos já normalizados do modelo (caminho rápido no hover).
export function pointsNearPosition(model, position, windowFraction = DEFAULT_RAIL_WINDOW_FRACTION) {
  if (!model?.valid || position == null || !Array.isArray(model.points)) return [];
  const half = windowFraction / 2;
  const lo = Math.max(0, position - half);
  const hi = Math.min(1, position + half);
  return model.points.filter((point) => point.position >= lo && point.position <= hi);
}

// events: [{ date, ... }]. Retorna subconjunto dentro da janela da posição.
export function eventsNearPosition(events, position, windowFraction, range) {
  if (!Array.isArray(events) || position == null) return [];
  const window = positionToMillisWindow(
    position,
    range?.start,
    range?.end,
    windowFraction,
  );
  if (!window) return [];
  return events.filter((event) => {
    const ms = toMillisFast(event?.date);
    return Number.isFinite(ms) && ms >= window.startMs && ms <= window.endMs;
  });
}

// Data do instante correspondente à posição na rail (sem alterar filtros).
export function dateAtRailPosition(position, start, end) {
  const startMs = toMillis(start);
  const endMs = toMillis(end);
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) return null;
  if (!Number.isFinite(position)) return null;
  return new Date(startMs + position * (endMs - startMs));
}

export function getRailLayerOpacity(date, position, range, windowFraction = DEFAULT_RAIL_WINDOW_FRACTION) {
  if (position == null) return null;
  return isDateNearPosition(date, position, windowFraction, range)
    ? RAIL_HIGHLIGHT_OPACITY
    : RAIL_DIMMED_OPACITY;
}
